// CliDownloadPage.jsx — 第一百零九批 D：dc-cli 的整包下载页（/cli）。
//
// ── 它回答的是一个新人真的会问的四问 ────────────────────────────────────────
// 「这是什么、我为什么需要它、下载完第一步做什么、我的 key 从哪来」。
// 第四问是那条死锁的另一半：CLI 要一枚 key 才能跑，而 key 现在只能从定向密递取——
// 所以这一页与 /secrets 是**一对**，少任何一块，一个新接入方在浏览器里都走不完
// 「拿到工具 + 拿到钥匙」。
//
// ⚠ **鸡生蛋**：他此刻**没有** key，所以第一步不可能是 `selfupdate`（那条命令要 key
// 才跑得起来）。三步的顺序因此是钉死的：解压 → 去「密件」取 key → 写进配置再 describe
// 自检；`cli.selfupdate` 只作为**装好之后**的补充出现。判据在 cliDownload.test.mjs：
// 谁把 selfupdate 排进三步里，那条当场红。
//
// ── 下载走普通 <a href>，不走 fetch ────────────────────────────────────────
// 同源 + cookie 会话（仓内先例：工单附件与 AdminReposPage 的下载）。fetch 一个 zip
// 再造 blob 会把整包读进内存，且下载进度、断点续传、浏览器的"另存为"全都要自己重造
// 一遍。⚠ href 里的路径必须是**整段字面量**——endpoint-callers 那条判据按字面量找
// 调用方，拼出来的路径它看不见，端点会被报成孤儿。
//
// ── 这一页不进导航 ──────────────────────────────────────────────────────────
// 导航横向预算在第一百零二/一百零四批就用满了，而这是一个一次性入口。入口从 /help
// 链过去（appNav.test.mjs 反向咬住：它不在 NAV_ENTRIES 里，且手册里必须有那条链接）。
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import HelpLink from '../components/HelpLink.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useT } from '../i18n/index.mjs';
import { CLI_STEP_KEYS, kbOf, normalizeCliManifest } from './cliDownload.mjs';

export default function CliDownloadPage() {
  const t = useT();
  const load = useLoad(() => api.cliManifest(), []);
  const m = normalizeCliManifest(load.data);
  const kb = kbOf(m.bundleBytes);

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('cli.title')}
        <HelpLink section="cli" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('cli.subtitle')}
      </Typography>

      {load.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : load.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(load.error, { byCode: { CLI_DIST_ABSENT: 'cli.err.distAbsent' },
                                    fallback: 'cli.error' }))}
          </Alert>
          <Button size="small" onClick={load.reload}>{t('common.retry')}</Button>
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
          >
            {m.version ? <Chip size="small" label={t('cli.version', { v: m.version })} /> : null}
            {m.hasBundle && kb != null ? (
              <Typography variant="caption" color="text.secondary">
                {t('cli.bundle.size', { n: m.fileCount, kb })}
              </Typography>
            ) : null}
          </Stack>

          {/* 下载直链。整段路径字面量（见头注）。`download` 让浏览器直接存盘而不是
              试图在标签页里打开一个二进制流。 */}
          {m.hasBundle ? (
            <Button variant="contained" component="a" href="/api/cli/bundle" download>
              {t('cli.download')}
            </Button>
          ) : (
            <Alert severity="warning">{t('cli.err.distAbsent')}</Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('cli.requirements')}
          </Typography>

          {/* 浏览器下载不留任何可核对的东西，而"我下的这份完整吗"是接入方第一个会问
              的问题。整包的 sha256 同时在响应头里（curl -I 一下就有）。 */}
          {m.hasBundle ? (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: 'ui-monospace, monospace',
                      overflowWrap: 'anywhere' }}
              >
                {t('cli.bundle.sha', { sha: m.bundleSha })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('cli.bundle.shaHint')}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('cli.steps.title')}
        </Typography>
        <Stack spacing={1.5}>
          {CLI_STEP_KEYS.map((key) => (
            <Box key={key}>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {t(key)}
              </Typography>
              {key === 'cli.steps.key' ? (
                <Button size="small" component={RouterLink} to="/secrets" sx={{ mt: 0.5 }}>
                  {t('shell.nav.secrets')}
                </Button>
              ) : null}
            </Box>
          ))}
        </Stack>
        <Divider sx={{ my: 2 }} />
        {/* selfupdate 刻意排在三步**之外**：他现在还没有 key，那条命令跑不起来。 */}
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {t('cli.selfupdate')}
        </Typography>
      </Paper>
    </Box>
  );
}
